import React, { useEffect, useMemo, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * LSV-Collection-Wizard (pain.008)
 * - Empfängt selectedInvoices, ruft Mandate ab
 * - User wählt Connection + Creditor-ID + Einzugsdatum
 * - Submit → POST /lsv-collection-batches
 */
export function LsvCollectionWizard({ open, onClose, companyId, selectedInvoices, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [connections, setConnections] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [connectionId, setConnectionId] = useState("");
  const [creditorId, setCreditorId] = useState(
    () => localStorage.getItem("lsv_creditor_id") || "",
  );
  const [collectionDate, setCollectionDate] = useState(today);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancel = false;
    (async () => {
      try {
        const [connRes, mRes] = await Promise.all([
          apiClient.get(`/companies/${companyId}/postfinance/connections`),
          apiClient.get(`/companies/${companyId}/lsv-mandates`, { params: { status: "active" } }),
        ]);
        if (cancel) return;
        const ready = (connRes.data || []).filter((c) => c.status === "ready");
        setConnections(ready);
        if (ready.length > 0) setConnectionId(ready[0].id);
        setMandates(mRes.data || []);
      } catch (e) {
        toast.error("Daten konnten nicht geladen werden.");
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open, companyId]);

  const lines = useMemo(() => {
    return (selectedInvoices || []).map((inv) => {
      const m = mandates.find((mm) => mm.customer_id === inv.customer_id && mm.status === "active");
      return {
        invoice_id: inv._id,
        invoice_number: inv.invoice_number,
        amount: parseFloat(inv.total) || 0,
        currency: inv.currency || "CHF",
        customer_id: inv.customer_id,
        mandate: m,
      };
    });
  }, [selectedInvoices, mandates]);

  const lacksMandate = lines.filter((l) => !l.mandate);
  const eligible = lines.filter((l) => l.mandate);
  const totals = useMemo(() => {
    const sum = eligible.reduce((acc, l) => acc + l.amount, 0);
    return { total: Math.round(sum * 100) / 100, count: eligible.length };
  }, [eligible]);

  const canSubmit =
    connectionId && creditorId.trim() && collectionDate && eligible.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    localStorage.setItem("lsv_creditor_id", creditorId.trim());
    try {
      const payload = {
        connection_id: connectionId,
        creditor_id: creditorId.trim(),
        requested_collection_date: collectionDate,
        upload_now: true,
        invoice_ids: eligible.map((l) => l.invoice_id),
      };
      const res = await apiClient.post(
        `/companies/${companyId}/postfinance/lsv-collection-batches`,
        payload,
      );
      const data = res.data;
      if (data.upload_status === "uploaded") {
        toast.success(
          `Lastschrift-Lauf erstellt: ${data.collection_count} Einzüge · ${data.total_amount.toFixed(2)} ${data.currency} · pain.008 hochgeladen`,
        );
      } else if (data.upload_status === "upload_failed") {
        toast.warning(
          `pain.008 erstellt, Upload fehlgeschlagen: ${data.upload_error || "Unbekannt"}.`,
        );
      } else {
        toast.info(`pain.008 erstellt (${data.upload_status})`);
      }
      onSuccess?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Erstellen des Einzugs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="lsv-collection-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-emerald-600" />
            Lastschrift-Lauf (LSV+)
          </DialogTitle>
          <DialogDescription>
            Erzeugt eine ISO-20022 pain.008-Datei für CH-DD und uploadet sie zu PostFinance.
            Voraussetzung: aktives Mandat pro Kunde.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>PostFinance-Verbindung</Label>
              <Select value={connectionId} onValueChange={setConnectionId}>
                <SelectTrigger data-testid="lsv-wizard-connection-select">
                  <SelectValue placeholder="Verbindung wählen" />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {c.environment === "test" ? "Test" : "Live"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Einzugsdatum</Label>
              <SwissDateField
                
                value={collectionDate}
                onChange={(e) => setCollectionDate(e.target.value)}
                data-testid="lsv-wizard-collection-date"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Creditor-ID (CHDD-Kennung von PostFinance)</Label>
              <Input
                value={creditorId}
                onChange={(e) => setCreditorId(e.target.value)}
                placeholder="z.B. CHDD123456789"
                data-testid="lsv-wizard-creditor-id"
              />
              <p className="text-[11px] text-slate-500 mt-0.5">
                Wird einmalig gemerkt (localStorage). Erhalten von PostFinance bei
                LSV+-Aktivierung.
              </p>
            </div>
          </div>

          {/* Lines */}
          <div className="border rounded-md overflow-hidden">
            <div className="text-xs font-medium bg-slate-100 px-3 py-2 border-b">
              Rechnungen ({lines.length})
            </div>
            <ul className="divide-y text-xs">
              {lines.map((l) => (
                <li
                  key={l.invoice_id}
                  className={`px-3 py-2 flex items-center justify-between ${
                    l.mandate ? "" : "bg-amber-50"
                  }`}
                  data-testid={`lsv-wizard-line-${l.invoice_id}`}
                >
                  <div className="font-mono">{l.invoice_number}</div>
                  <div className="flex-1 px-3 text-slate-600">
                    {l.mandate ? (
                      <>
                        Mandat {l.mandate.mandate_id} · {l.mandate.debtor_name}
                      </>
                    ) : (
                      <span className="text-amber-800 font-medium">
                        Kein aktives Mandat — wird übersprungen
                      </span>
                    )}
                  </div>
                  <div className="font-medium">
                    {l.amount.toFixed(2)} {l.currency}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {lacksMandate.length > 0 && (
            <div className="flex gap-2 items-start text-xs rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>{lacksMandate.length}</strong> Rechnung(en) ohne aktives Mandat — werden
                automatisch übersprungen. Lege das Mandat in <em>LSV-Mandate</em> an.
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="text-sm text-emerald-900">
              <span className="font-semibold">{totals.count}</span> Einzug/Einzüge ·{" "}
              <span className="font-semibold">{totals.total.toFixed(2)} CHF</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              ISO-20022 pain.008 (CH-DD)
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5"
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="lsv-wizard-submit-button"
            >
              <Send className="h-4 w-4 mr-1" />
              {submitting ? "Erzeuge pain.008 …" : "Einzug erstellen & hochladen"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
